import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Mail, Phone, MapPin, Send, Clock, CheckCircle2, Loader2,
  GraduationCap, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import BackNavigation from "@/components/BackNavigation";
import { useCMSContent } from "@/hooks/useCMSContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cinematic visual assets - 4K UHD Human-Centered (PROTECTED - DO NOT MODIFY)
const CINEMATIC_VISUALS = {
  heroVideo: "https://videos.pexels.com/video-files/3196487/3196487-uhd_2560_1440_25fps.mp4",
  heroPoster: "https://images.pexels.com/photos/7682201/pexels-photo-7682201.jpeg?w=1920"
};

// Fallback content for CMS
const FALLBACK_CONTENT = {
  hero: {
    title: "Get In Touch",
    description: "Have questions about our programs, research, or partnership opportunities? We're here to help."
  }
};

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  // Fetch CMS content with fallback
  const { content } = useCMSContent('contact', FALLBACK_CONTENT);
  const hero = content.hero || FALLBACK_CONTENT.hero;
  
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = 0.6;
      const handleLoaded = () => setVideoLoaded(true);
      video.addEventListener('loadeddata', handleLoaded);
      return () => video.removeEventListener('loadeddata', handleLoaded);
    }
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/contact/send`, {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message
      });
      
      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll be in touch soon.");
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactItems = [
    {
      icon: Mail,
      title: "General Information",
      detail: "info@zodiacinstitute.org",
      color: "#991B1B",
      isEmail: true
    },
    {
      icon: GraduationCap,
      title: "Admissions Office",
      detail: "admission@zodiacinstitute.org",
      color: "#1E3A8A",
      isEmail: true
    },
    {
      icon: FileText,
      title: "Registrar Office",
      detail: "registrar@zodiacinstitute.org",
      color: "#16A34A",
      isEmail: true
    },
    {
      icon: Phone,
      title: "Phone",
      detail: "786-499-0171",
      color: "#991B1B",
      isPhone: true
    },
    {
      icon: MapPin,
      title: "Address",
      detail: "515 SW 24th Street, San Antonio, Texas 78207",
      color: "#991B1B"
    },
    {
      icon: Clock,
      title: "Office Hours",
      detail: "Monday - Friday: 8:00 AM - 6:00 PM",
      detail2: "Saturday: 9:00 AM - 2:00 PM",
      color: "#991B1B"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="contact-page">
      {/* Back Navigation */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero - Cinematic Video Background */}
      <section className="relative overflow-hidden min-h-[50vh] flex items-center -mt-12">
        {/* Video Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{ 
              backgroundImage: `url(${CINEMATIC_VISUALS.heroPoster})`,
              opacity: videoLoaded ? 0 : 1
            }}
          />
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            poster={CINEMATIC_VISUALS.heroPoster}
          >
            <source src={CINEMATIC_VISUALS.heroVideo} type="video/mp4" />
          </video>
          {/* Gradient Overlay - 30% opacity */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/35 via-[#1E3A8A]/30 to-[#0F172A]/25" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-4" data-testid="contact-hero-title">
              {hero.title}
            </h1>
            <p className="text-lg text-white/80 leading-relaxed" data-testid="contact-hero-description">
              {hero.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Contact Information Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl font-bold mb-8" style={{ color: '#991B1B' }}>
                Contact Information
              </h2>
              
              <div className="space-y-5">
                {contactItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4"
                  >
                    {/* Icon Tile */}
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: item.color }}
                    >
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    
                    {/* Content */}
                    <div className="pt-1">
                      <h3 className="font-semibold text-[#111827] mb-1">
                        {item.title}
                      </h3>
                      {item.isEmail ? (
                        <a 
                          href={`mailto:${item.detail}`}
                          className="text-[#1E3A8A] hover:text-[#991B1B] transition-colors"
                        >
                          {item.detail}
                        </a>
                      ) : item.isPhone ? (
                        <a 
                          href={`tel:${item.detail.replace(/-/g, '')}`}
                          className="text-[#1E3A8A] hover:text-[#991B1B] transition-colors"
                        >
                          {item.detail}
                        </a>
                      ) : (
                        <>
                          <p className="text-[#1E3A8A]">{item.detail}</p>
                          {item.detail2 && (
                            <p className="text-[#1E3A8A]">{item.detail2}</p>
                          )}
                        </>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Contact Message Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl font-bold mb-8" style={{ color: '#991B1B' }}>
                Send Us a Message
              </h2>

              {isSubmitted ? (
                <Card className="border-[#E5E7EB] bg-white">
                  <CardContent className="p-10 text-center">
                    <div className="w-16 h-16 rounded-full bg-[#16A34A]/10 mx-auto mb-6 flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-[#16A34A]" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold mb-3" style={{ color: '#991B1B' }}>
                      Message Sent!
                    </h3>
                    <p className="text-[#1E3A8A] mb-6">
                      Thank you for contacting us. We'll respond to your inquiry within 1-2 business days.
                    </p>
                    <Button 
                      onClick={() => {
                        setIsSubmitted(false);
                        setFormData({ name: "", email: "", subject: "", message: "" });
                      }}
                      variant="outline"
                      className="border-[#E5E7EB]"
                    >
                      Send Another Message
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-[#E5E7EB] bg-white">
                  <CardContent className="p-6 md:p-8">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name <span className="text-[#991B1B]">*</span></Label>
                        <Input
                          id="name"
                          placeholder="Your full name"
                          value={formData.name}
                          onChange={(e) => handleChange("name", e.target.value)}
                          required
                          className="bg-white h-11"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address <span className="text-[#991B1B]">*</span></Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          required
                          className="bg-white h-11"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject <span className="text-[#991B1B]">*</span></Label>
                        <Input
                          id="subject"
                          placeholder="What is this regarding?"
                          value={formData.subject}
                          onChange={(e) => handleChange("subject", e.target.value)}
                          required
                          className="bg-white h-11"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Message <span className="text-[#991B1B]">*</span></Label>
                        <Textarea
                          id="message"
                          placeholder="Your message..."
                          value={formData.message}
                          onChange={(e) => handleChange("message", e.target.value)}
                          required
                          rows={5}
                          className="bg-white resize-none"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full bg-[#991B1B] text-white hover:bg-[#7F1D1D] h-12 rounded-lg text-base"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 lg:py-16 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl font-bold mb-6 text-center" style={{ color: '#991B1B' }}>
              Our Location
            </h2>
            <p className="text-center text-[#1E3A8A] mb-8">
              515 SW 24th Street, San Antonio, Texas 78207
            </p>
            
            <div className="rounded-xl overflow-hidden border border-[#E5E7EB] shadow-sm">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3474.8901234567!2d-98.5100000!3d29.4100000!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x865c5f5a3d0e5b1b%3A0x1234567890abcdef!2s515%20SW%2024th%20St%2C%20San%20Antonio%2C%20TX%2078207!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Zodiac Workforce Intelligence Institute Location"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
